import { Box, Stack, styled, Tooltip, Typography } from "@mui/material";
import { FC, MouseEventHandler } from "react";
import numeral from "numeral";
import { PrimaryClassification } from "@/gen/api/encrypt";
import { ReduxState, ShowBase, useRecordContext, useResourceContext } from "ra-core";
import { osfCatalogRes, useFileCataSearchContext } from "./FileCataSearchContextProvider";
import { PrimaryClassificationStatisticsResponseDto } from "@/gen/api/securefile";
import { useSelector } from "react-redux";



const BarItemRoot = styled(Box)({
  height: 48,
  position: 'relative',
  cursor: 'pointer',

  '& div.bar': {
    width: 12,
    borderRadius: 'unset',
  },
  '& div.bar-label': {
    position: 'absolute',
    width: 12,
    '& p': {
      textAlign: 'center',
    }
  },
  '& div.bar-number': {
    display: 'none',
    position: 'absolute',
    top: -20,
    width: 12,
    '& p': {
      textAlign: 'center',
    }
  },
  '&:hover div.bar-number': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
})

const BarButtonRoot = styled(Box)({

})


interface BarProps {
  proportion: number,
  count: number,
  color: string,
  onClick: MouseEventHandler,
  label: string,
  title?: string,
}

const BarItem: FC<BarProps> = ({
  proportion,
  count,
  color,
  onClick,
  label,
  title,
}) => {
  return (
    <Tooltip title={title || label}>
      <BarItemRoot onClick={onClick}>
        <Box className="bar-number">
          <Typography variant="body2" color="info.main">{numeral(count).format('0,0')}</Typography>
        </Box>
        <Box className="space-holder" sx={{ height: `${100 - proportion}%` }}></Box>
        <BarButtonRoot className="bar" sx={{ bgcolor: color, height: `${proportion}%` }} />
        <Box className="bar-label">
          <Typography variant="body2" color="info.main">{label}</Typography>
        </Box>
      </BarItemRoot>
    </Tooltip>
  )
}


const FileCataClassificationStatistics: FC = () => {
  const resRegistered = useSelector<ReduxState>(store => !!store.admin.resources['secure-files/file-catalog-statis'])
  const listRes = useResourceContext()
  const resource = listRes === osfCatalogRes ? 'secure-files/file-catalog-statis' : 'secure-files/pfs-statis'
  
  if (!resRegistered) { return null }
  return (
    <ShowBase resource={resource} id="statis" basePath="">
      <FileCataClassificationStatisticsInner />
    </ShowBase>
  )
}

const FileCataClassificationStatisticsInner: FC = () => {
  const classificationStatistics : PrimaryClassificationStatisticsResponseDto|undefined = useRecordContext() as any
  const { addPrimaryClassification } = useFileCataSearchContext()

  const pickClassification = (c?: PrimaryClassification) => () => {
    addPrimaryClassification(c ?? null)
  }
  
  const counts = [
    classificationStatistics?.publicCount || 0,
    classificationStatistics?.sensitiveCount || 0,
    classificationStatistics?.confidentialCount || 0,
    classificationStatistics?.regulatedCount || 0,
    classificationStatistics?.unclassifiedCount || 0,
  ]
  const max = Math.max(...counts, 1)
  const propotions = counts.map(i => Math.max((i * 100 / max)^0, 1))

  return (
    <Stack direction="row" alignItems="baseline" sx={{ mb: 4 }}>
      <Typography variant="body1" color="text.secondary">Classifications:</Typography>
      <Stack direction="row" sx={{ pl: 1 }} spacing={1}>
        <BarItem 
          label="P"
          title="Public"
          color={ChartColorScheme[PrimaryClassification.Public]}
          proportion={propotions[0]}
          count={counts[0]}
          onClick={pickClassification(PrimaryClassification.Public)}
        />
        <BarItem 
          label="S"
          title="Sensitive"
          color={ChartColorScheme[PrimaryClassification.Sensitive]}
          proportion={propotions[1]}
          count={counts[1]}
          onClick={pickClassification(PrimaryClassification.Sensitive)}
        />
        <BarItem 
          label="C"
          title="Confidential"
          color={ChartColorScheme[PrimaryClassification.Confidential]}
          proportion={propotions[2]}
          count={counts[2]}
          onClick={pickClassification(PrimaryClassification.Confidential)}
        />
        <BarItem 
          label="R"
          title="Regulated"
          color={ChartColorScheme[PrimaryClassification.Regulated]}
          proportion={propotions[3]}
          count={counts[3]}
          onClick={pickClassification(PrimaryClassification.Regulated)}
        />
        <BarItem 
          label="-"
          title="Not Classified"
          color={ChartColorScheme['null']}
          proportion={propotions[4]}
          count={counts[4]}
          onClick={pickClassification()}
        />
      </Stack>
    </Stack>
  )
}

export default FileCataClassificationStatistics;

export const ChartColorScheme = {
  [PrimaryClassification.Public]: "#90DD75",
  [PrimaryClassification.Sensitive]: '#7886a5',//"#DBDBDB",
  [PrimaryClassification.Confidential]: "#432D14",
  [PrimaryClassification.Regulated]: "#0013C3",
  'null': "#E50E00",
}